/**
 * Tiny i18n runtime. Locale resolution: ?lang= → localStorage → navigator.language → 'en'.
 *
 * Bundles are imported eagerly — both locales are <5 KB gzipped, code-splitting
 * would cost more in flicker (string-less first paint) than it saves.
 *
 * `t(key, vars?)` substitutes `{name}` placeholders. Missing keys fall back to
 * English; missing vars render as the literal placeholder so the gap is
 * visible in QA. Pluralization uses key suffixes (`*_one`, `*_many`) selected
 * by `Intl.PluralRules` — see `tn()`.
 */
import { en } from './locales/en.ts';
import { is } from './locales/is.ts';
import type { BlueprintCategory } from '../blueprints/types.ts';

export type MessageKey = keyof typeof en;
/** Every locale must satisfy this shape. tsc enforces parity. */
export type Messages = Record<MessageKey, string>;

export const SUPPORTED_LOCALES = ['en', 'is'] as const;
export type Locale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: Locale = 'en';

const BUNDLES: Record<Locale, Messages> = { en, is };
const STORAGE_KEY = 'bb.locale';

let active: Locale = DEFAULT_LOCALE;

function isLocale(value: string | null | undefined): value is Locale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function detectFromQuery(): Locale | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('lang');
  return isLocale(raw) ? raw : null;
}

function detectFromStorage(): Locale | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

function detectFromNavigator(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const lang of langs) {
    const base = lang.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return null;
}

/** Pick a locale from URL/storage/navigator and apply it. Call once at boot. */
export function initLocale(): Locale {
  const picked =
    detectFromQuery() ?? detectFromStorage() ?? detectFromNavigator() ?? DEFAULT_LOCALE;
  setLocale(picked, { persist: false, updateUrl: false });
  return picked;
}

export function getLocale(): Locale {
  return active;
}

/** Swap the active locale. Persists to localStorage and (optionally) updates the URL. */
export function setLocale(
  locale: Locale,
  opts: { persist?: boolean; updateUrl?: boolean } = {},
): void {
  active = locale;
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
  if (opts.persist !== false) {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Private browsing / quota — ignore; URL is the durable signal.
    }
  }
  if (opts.updateUrl && typeof window !== 'undefined') {
    const url = new URL(window.location.href);
    if (locale === DEFAULT_LOCALE) url.searchParams.delete('lang');
    else url.searchParams.set('lang', locale);
    window.history.replaceState(null, '', url.toString());
  }
}

const PLACEHOLDER = /\{(\w+)\}/g;

/** Translate a key. Missing-locale keys fall through to English. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = BUNDLES[active][key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(PLACEHOLDER, (match, name) => {
    const v = vars[name];
    return v === undefined ? match : String(v);
  });
}

/** Pluralized lookup: picks `${baseKey}_one` or `${baseKey}_many` via Intl.PluralRules.
 *  Always passes `count` as a placeholder so the chosen template can render it. */
export function tn(
  baseKey: string,
  count: number,
  vars?: Record<string, string | number>,
): string {
  const rule = new Intl.PluralRules(active).select(count);
  // Map ICU categories to the two buckets we author (`one`, `many`). Anything
  // that isn't strictly "one" lands on "many" — matches the v1 string list.
  const suffix = rule === 'one' ? 'one' : 'many';
  const key = `${baseKey}_${suffix}` as MessageKey;
  return t(key, { count, ...(vars ?? {}) });
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(active).format(value);
}

/** Translate a blueprint category id (e.g. 'animals') via the `category.*` keys. */
export function categoryLabel(category: BlueprintCategory): string {
  return t(`category.${category}` as MessageKey);
}

/**
 * Resolve a blueprint's display name via the `blueprint_name.<id>` keys.
 * Falls back to the canonical `name` field on the Blueprint when no key
 * exists — handles user-imported blueprints whose ids aren't in the catalog.
 */
export function blueprintName(id: string, fallback: string): string {
  const key = `blueprint_name.${id}` as MessageKey;
  const result = t(key);
  // t() returns the key literal when no translation (active or English) exists.
  return result === key ? fallback : result;
}
